import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnInit,
  signal,
  Injector,
  viewChild,
  ElementRef,
  WritableSignal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule, FormGroup, FormControl, Validators } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { IconComponent } from '@app/components/shared/icon/icon.component';
import { IconName } from '@app/utils/enums/icon-enum';
import { CONFIRM_DIALOG_OPTIONS } from '@app/utils/constants/confirm.constants';
import { ConfirmModalComponent } from '@app/components/shared/confirm-modal/confirm-modal.component';
import { ConnectedPosition, Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { DropdownOverlayComponent } from './dropdown-overlay/dropdown-overlay.component';
import { OVERLAY_DATA } from './overlay.constants';
import { TranslateModule } from '@ngx-translate/core';
import { InputFieldComponent } from '@app/components/shared/form-controls/input-field/input-field.component';
import { CreateStaffPayload, Role } from '@app/utils/interfaces/staff.interface';
import { DropdownFieldComponent } from './dropdown-field/dropdown-field.component';
import { catchError, EMPTY, finalize, tap } from 'rxjs';
import { StaffService } from '@app/pages/staff/services/staff.service';
import { ErrorModalComponent } from '@app/components/shared/error-modal/error-modal.component';
import { RolesService } from '@app/pages/home-page/shop-settings/settings-roles/services/roles.service';

const ROLES_PAGE_NUMBER = 1;
const ROLES_PAGE_SIZE = 100;

@Component({
  selector: 'app-create-staff-dialog',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    IconComponent,
    InputFieldComponent,
    TranslateModule,
    DropdownFieldComponent,
  ],
  templateUrl: './create-staff-dialog.component.html',
  styleUrl: './create-staff-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreateStaffDialogComponent implements OnInit {
  protected readonly IconName = IconName;
  protected readonly dialogRef = inject(MatDialogRef);
  protected readonly dialog = inject(MatDialog);
  protected readonly staffService = inject(StaffService);
  protected readonly rolesService = inject(RolesService);
  private readonly destroyRef = inject(DestroyRef);
  protected readonly overlay = inject(Overlay);
  protected overlayRef: OverlayRef | null = null;
  protected readonly injector = inject(Injector);
  protected readonly roleInputRef = viewChild<ElementRef>('roleInput');

  protected readonly roles = signal<Role[]>([]);
  protected readonly filteredRoles = signal<Role[]>([]);
  protected readonly dropdownOpen = signal(false);
  protected readonly isSubmitting = signal(false);

  public readonly addStaffForm = new FormGroup({
    firstName: new FormControl('', [
      Validators.required,
      Validators.minLength(2),
      Validators.maxLength(30),
      Validators.pattern('^[A-Z][a-z]+(?: [A-Z][a-z]+)*$')
    ]),
    lastName: new FormControl('', [
      Validators.required,
      Validators.minLength(2),
      Validators.maxLength(30),
      Validators.pattern('^[A-Z][a-z]+(?: [A-Z][a-z]+)*$')
    ]),
    email: new FormControl('', [Validators.required, Validators.email]),
    role: new FormControl('', [Validators.required]),
  });

  public ngOnInit(): void {
    this.loadRoles();

    this.addStaffForm
      .get('role')
      ?.valueChanges.pipe(
        takeUntilDestroyed(this.destroyRef),
        tap((value) => this.filerRoles(value)),
      )
      .subscribe();
  }

  public onSubmit(): void {
    this.addStaffForm.markAllAsTouched();

    if (this.addStaffForm.invalid) {
      return;
    }

    const { firstName, lastName, email, role } = this.addStaffForm.getRawValue();
    const selectedRole = this.roles().find(
      (roleItem) => roleItem.name.toLowerCase() === role?.trim().toLowerCase(),
    );

    if (!selectedRole) {
      this.addStaffForm.get('role')?.setErrors({ roleNotFound: true });
      return;
    }

    const payload: CreateStaffPayload = {
      firstName: firstName?.trim() ?? '',
      lastName: lastName?.trim() ?? '',
      email: email?.trim() ?? '',
      roleId: selectedRole.id,
    };

    this.isSubmitting.set(true);

    this.staffService
      .createStaff(payload)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap(() => this.dialogRef.close(true)),
        catchError(() => {
          this.openErrorModal('staff.table.errors.staffLoadFail');
          return EMPTY;
        }),
        finalize(() => this.isSubmitting.set(false)),
      )
      .subscribe();
  }

  public displayError(controlName: string): string {
    const control = this.addStaffForm.get(controlName);
    const value = control?.value.trim();
    if (control?.touched && control?.invalid && value === '') {
      return 'empty';
    } else if (control?.dirty && control?.invalid) {
      return 'invalid';
    }
    return 'valid';
  }

  public selectRole(role: { name: string | null }): void {
    const control = this.addStaffForm.get('role');
    control?.setValue(role.name);
    this.closeOverlay();
  }

  public filerRoles(inputValue: string | null | undefined): void {
    const searchString = inputValue?.toLowerCase() || '';

    this.filteredRoles.set(
      this.roles().filter((role) => role.name.toLowerCase().includes(searchString)),
    );
  }

  public onFocus(): void {
    const control = this.addStaffForm.get('role');

    const overlayData = {
      roles: this.filteredRoles,
      onSelect: (role: { name: string | null }) => this.selectRole(role),
      selectedValue: control?.valid ? control?.value : '',
    };

    this.dropdownOpen.set(true);
    this.openOverlay(overlayData);
  }

  public closeOverlay(): void {
    this.overlayRef?.detach();
    this.dropdownOpen.set(false);
  }

  public onClose(): void {
    if (this.addStaffForm.untouched && this.addStaffForm.pristine) {
      this.dialogRef.close();
      return;
    }

    const confirmRef = this.dialog.open(ConfirmModalComponent, {
      ...CONFIRM_DIALOG_OPTIONS,
      panelClass: 'confirm-dialog',
    });

    this.dialogRef.addPanelClass('hidden-dialog');

    confirmRef
      .afterClosed()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap((confirmed) => {
          if (confirmed) {
            this.dialogRef.close();
          } else {
            this.dialogRef.removePanelClass('hidden-dialog');
          }
        }),
      )
      .subscribe();
  }

  private openOverlay(dataToPass: {
    roles: WritableSignal<Role[]>;
    onSelect: (role: { name: string | null }) => void;
    selectedValue: string | null;
  }): void {
    this.overlayRef?.detach();

    const positions: ConnectedPosition[] = [
      {
        originX: 'start',
        originY: 'bottom',
        overlayX: 'start',
        overlayY: 'top',
        offsetY: 4,
      },
      {
        originX: 'start',
        originY: 'top',
        overlayX: 'start',
        overlayY: 'bottom',
        offsetY: -4,
      },
    ];

    this.overlayRef = this.overlay.create({
      positionStrategy: this.overlay
        .position()
        .flexibleConnectedTo(this.roleInputRef()?.nativeElement)
        .withPositions(positions),
    });

    const customInjector = Injector.create({
      parent: this.injector,
      providers: [{ provide: OVERLAY_DATA, useValue: dataToPass }],
    });

    const dropdownPortal = new ComponentPortal(DropdownOverlayComponent, null, customInjector);
    this.overlayRef?.attach(dropdownPortal);
  }

  private loadRoles(): void {
    this.rolesService
      .getRoles(ROLES_PAGE_NUMBER, ROLES_PAGE_SIZE)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap((response) => {
          const roles = response.data.items.map(({ id, name }) => ({ id, name }));

          this.roles.set(roles);
          this.filteredRoles.set(roles);
        }),
        catchError(() => {
          this.openErrorModal('staff.edit.errors.system');
          return EMPTY;
        }),
      )
      .subscribe();
  }

  private openErrorModal(message: string): void {
    this.dialog.open(ErrorModalComponent, {
      width: '400px',
      data: {
        message,
      },
    });
  }
}
